import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Redirect } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCreateOrgMutation } from '@/queries/auth/useCreateOrgMutation';
import { useAuthStore } from '@/stores/auth-store';
import { cn } from '@/lib/utils';
import {
  getPasswordRequirementState,
  validateNewPassword,
} from '@/lib/password-validation';

const EMAIL_RE = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+$/;

function optionalUrl(value) {
  const v = value?.trim();
  if (!v) {
    return undefined;
  }
  try {
    new URL(v.includes('://') ? v : `https://${v}`);
    return undefined;
  } catch {
    return 'Enter a valid URL';
  }
}

export default function Signup() {
  const createOrgMutation = useCreateOrgMutation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      accountName: '',
      phoneNumber: '',
      orgName: '',
      websiteAddress: '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  });

  const password = watch('password');
  const { lenOk, capOk, specOk } = getPasswordRequirementState(password);

  const submit = (values) => {
    createOrgMutation.mutate(values);
  };

  if (isAuthenticated) {
    return <Redirect to="/" />;
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] px-2 py-4 md:px-4">
      <nav aria-label="Main Navigation" className="mb-8">
        <div className="text-xl font-bold tracking-tight text-[#121212]">
          Festyland
        </div>
      </nav>

      <form
        className="admin-form grid gap-8 lg:grid-cols-[1fr_minmax(0,280px)] lg:items-start"
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <div className="event-main-info flex min-w-0 flex-col gap-6">
          <div className="glass-panel glass-panel-interactive p-6 sm:p-8">
            <h1 className="mb-2 text-left text-[clamp(1.35rem,2vw+0.8rem,1.85rem)] font-bold text-[#121212]">
              Create Your Account
            </h1>
            <p className="mb-6 text-left text-sm text-[var(--muted-foreground)]">
              Join Us to start managing your events.
            </p>
            <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-[#121212]">
              <FontAwesomeIcon
                icon={['fas', 'user']}
                className="text-[var(--bethere-primary)]"
              />
              Personal Information
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-4">
              <div className="input-group">
                <label htmlFor="signup-first">First Name</label>
                <input
                  id="signup-first"
                  type="text"
                  placeholder="Alex"
                  autoComplete="given-name"
                  {...register('firstName', { required: 'Required' })}
                />
                <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                  {errors.firstName?.message}
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="signup-last">Last Name</label>
                <input
                  id="signup-last"
                  type="text"
                  placeholder="Noble"
                  autoComplete="family-name"
                  {...register('lastName', { required: 'Required' })}
                />
                <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                  {errors.lastName?.message}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-4">
              <div className="input-group">
                <label htmlFor="signup-email">Email Address</label>
                <input
                  id="signup-email"
                  type="email"
                  placeholder="alex@nobleapplications.com"
                  autoComplete="email"
                  {...register('accountName', {
                    required: 'Required',
                    validate: (value) =>
                      EMAIL_RE.test(value?.trim() ?? '') ||
                      'Enter a valid email address',
                  })}
                />
                <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                  {errors.accountName?.message}
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="signup-phone">Phone Number</label>
                <input
                  id="signup-phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  autoComplete="tel"
                  {...register('phoneNumber')}
                />
              </div>
            </div>
          </div>

          <div className="glass-panel glass-panel-interactive p-6 sm:p-8">
            <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-[#121212]">
              <FontAwesomeIcon
                icon={['fas', 'building']}
                className="text-[var(--bethere-primary)]"
              />
              Organization Details
            </h2>
            <div className="input-group">
              <label htmlFor="signup-org-name">Organization Name</label>
              <input
                id="signup-org-name"
                type="text"
                placeholder="e.g. Noble Applications Inc."
                autoComplete="organization"
                {...register('orgName', { required: 'Required' })}
              />
              <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                {errors.orgName?.message}
              </div>
            </div>
            <div className="input-group">
              <label htmlFor="signup-website">Organization Website</label>
              <input
                id="signup-website"
                type="url"
                placeholder="https://nobleapplications.com"
                autoComplete="url"
                {...register('websiteAddress', {
                  validate: optionalUrl,
                })}
              />
              <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                {errors.websiteAddress?.message}
              </div>
            </div>
          </div>

          <div className="glass-panel glass-panel-interactive p-6 sm:p-8">
            <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-[#121212]">
              <FontAwesomeIcon
                icon={['fas', 'lock']}
                className="text-[var(--bethere-primary)]"
              />
              Security
            </h2>
            <div className="input-group">
              <label htmlFor="signup-password">Password</label>
              <div className="relative">
                <input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  autoComplete="new-password"
                  className="pr-12"
                  {...register('password', {
                    validate: validateNewPassword,
                  })}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-[var(--muted-foreground)] hover:text-[#121212]"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <FontAwesomeIcon
                    icon={['fas', showPassword ? 'eye-slash' : 'eye']}
                  />
                </button>
              </div>
              <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                {errors.password?.message}
              </div>
            </div>
            <ul className="mt-2 list-none space-y-1 p-0 text-sm">
              <li
                className={cn(
                  'mb-1 flex items-center gap-2',
                  lenOk
                    ? 'text-[var(--bethere-success)]'
                    : 'text-[var(--muted-foreground)]',
                )}
              >
                <FontAwesomeIcon icon={['fas', 'circle-check']} />8 Characters
              </li>
              <li
                className={cn(
                  'mb-1 flex items-center gap-2',
                  capOk
                    ? 'text-[var(--bethere-success)]'
                    : 'text-[var(--muted-foreground)]',
                )}
              >
                <FontAwesomeIcon icon={['fas', 'circle-check']} />
                One Capitalized Letter
              </li>
              <li
                className={cn(
                  'flex items-center gap-2',
                  specOk
                    ? 'text-[var(--bethere-success)]'
                    : 'text-[var(--muted-foreground)]',
                )}
              >
                <FontAwesomeIcon icon={['fas', 'circle-check']} />
                One Special Character
              </li>
            </ul>
            <div className="input-group mt-4">
              <label htmlFor="signup-confirm">Confirm password</label>
              <input
                id="signup-confirm"
                type="password"
                placeholder="Repeat password"
                autoComplete="new-password"
                {...register('confirmPassword', {
                  required: 'Required',
                  validate: (value, formValues) =>
                    value === formValues.password || 'Passwords do not match',
                })}
              />
              <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                {errors.confirmPassword?.message}
              </div>
            </div>
          </div>
        </div>

        <aside className="flex min-w-0 flex-col gap-4 self-start lg:sticky lg:top-8">
          <div className="glass-panel glass-panel-interactive p-6 text-left">
            <h3 className="mb-3 text-lg font-bold text-[#121212]">Join Us</h3>
            <p className="mb-6 text-sm leading-relaxed text-[var(--muted-foreground)]">
              By creating an account, you agree to our{' '}
              <a href="#" className="font-bold text-[var(--bethere-primary)]">
                terms of service
              </a>{' '}
              and{' '}
              <a href="#" className="font-bold text-[var(--bethere-primary)]">
                privacy policy
              </a>
              .
            </p>
            <div className="flex flex-col gap-3">
              <button
                type="submit"
                disabled={isSubmitting || createOrgMutation.isPending}
                className="inline-flex w-full min-h-[52px] items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] bg-[var(--bethere-accent)] font-bold text-[#121212] shadow-sm transition hover:bg-[var(--bethere-secondary)] hover:shadow-md disabled:opacity-60"
              >
                <FontAwesomeIcon icon={['fas', 'user-plus']} />
                Create Account
              </button>
              <Link
                to="/login"
                className="flex min-h-[52px] w-full items-center justify-center rounded-[var(--bethere-radius-md)] border border-[var(--input)] bg-white/60 text-center font-bold text-[#121212] shadow-sm transition hover:bg-white/90"
              >
                Cancel
              </Link>
            </div>
          </div>
          <div className="glass-panel p-4 text-center">
            <p className="text-sm text-[var(--muted-foreground)]">
              Already have an account?
            </p>
            <Link
              to="/login"
              className="mt-1 inline-block text-sm font-bold text-[var(--bethere-primary)] hover:underline"
            >
              Sign in here
            </Link>
          </div>
        </aside>
      </form>

      <footer className="mt-10 text-center text-sm text-[var(--muted-foreground)]">
        © {new Date().getFullYear()} Noble Applications | Admin Portal
      </footer>
    </div>
  );
}
